import { skipToken } from '@reduxjs/toolkit/query/react';
import useLocalStorage from 'hooks/useLocalStorage';
import { useMemo } from 'react';
import {
  useGetShipmentsClientV2Query,
  useGetShipmentLinesQuery,
} from 'store/slices/clientV2/shipments';
import {
  warehouseDefaults,
  warehouseStorageName,
} from 'types/warehouseSettings';
import useServerSideQuery from 'hooks/useServerSideQuery';
import {
  DISABLE_PAGE_LIMIT,
  ALL,
  QueryFilterOperators,
  NullOperatorValues,
  Lite,
} from 'types/api';
import {
  ShipmentLineSchema,
  ShipmentLineSchemaLite,
  ShipmentSchema,
} from 'types/clientV2/shipments';
import { ProductionRunLite } from 'types/production';
import { ShipmentType } from 'types/inventory';

type ShipmentLineToReceive = Pick<ShipmentLineSchemaLite, 'id' | 'shipment'>;

type ShipmentToReceiveQuery = Lite<ShipmentSchema, 'shipTo' | 'shipFrom'> & {
  productionRun: Pick<ProductionRunLite, 'name'>;
};

export type ShipmentToReceive = ShipmentToReceiveQuery & {
  lines: ShipmentLineToReceive[];
  shipmentType: ShipmentType;
};

export type UseGetShipmentsToReceiveReturnType = {
  shipmentsToReceive: ShipmentToReceive[];
  isGetShipmentsToReceiveSuccess: boolean;
  isFetchingShipmentsToReceive: boolean;
  isFetchingShipmentsToReceiveError: boolean;
  refetchShipmentsToReceive: () => void;
};

export const getShipmentType = (
  shipment: ShipmentToReceiveQuery
): ShipmentType => {
  const { shipFrom, shipTo } = shipment;

  if (
    shipFrom &&
    shipFrom.warehouseShippingEnabled &&
    shipTo &&
    !shipTo.warehouseShippingEnabled &&
    shipTo.id !== shipFrom.id
  ) {
    return ShipmentType.WAREHOUSE_TO_NONWAREHOUSE;
  }
  if (
    shipFrom &&
    !shipFrom.warehouseShippingEnabled &&
    shipTo &&
    shipTo.warehouseShippingEnabled &&
    shipTo.id !== shipFrom.id
  ) {
    return ShipmentType.NONWAREHOUSE_TO_WAREHOUSE;
  }
  if (!shipFrom && shipTo && shipTo.warehouseShippingEnabled) {
    return ShipmentType.NONWAREHOUSE_TO_WAREHOUSE;
  }
  if (
    shipFrom &&
    shipFrom.warehouseShippingEnabled &&
    shipTo &&
    shipTo.warehouseShippingEnabled &&
    shipTo.id !== shipFrom.id
  ) {
    return ShipmentType.WAREHOUSE_TO_WAREHOUSE;
  }
  if (
    shipFrom &&
    !shipFrom.warehouseShippingEnabled &&
    shipTo &&
    !shipTo.warehouseShippingEnabled &&
    shipTo.id !== shipFrom.id
  ) {
    return ShipmentType.NONWAREHOUSE_TO_NONWAREHOUSE;
  }
  if (!shipFrom && shipTo && !shipTo.warehouseShippingEnabled) {
    return ShipmentType.NONWAREHOUSE_TO_NONWAREHOUSE;
  }
  if (
    shipFrom &&
    shipFrom.warehouseShippingEnabled &&
    shipTo &&
    shipTo.warehouseShippingEnabled &&
    shipTo.id === shipFrom.id
  ) {
    return ShipmentType.KIT_WITHIN_WAREHOUSE;
  }
  return ShipmentType.UNSPECIFIED;
};

export const useGetShipmentsToReceive =
  (): UseGetShipmentsToReceiveReturnType => {
    const [settings] = useLocalStorage(warehouseStorageName, warehouseDefaults);

    const {
      data: shipmentsToReceiveResponse,
      isSuccess: isGetShipmentsToReceiveSuccess,
      isFetching: isFetchingShipmentsToReceive,
      isError: isFetchingShipmentsToReceiveError,
      refetch: refetchShipmentsToReceive,
    } = useServerSideQuery<ShipmentSchema, ShipmentToReceiveQuery>(
      useGetShipmentsClientV2Query,
      settings.facility
        ? {
            pageSize: DISABLE_PAGE_LIMIT,
            schema: [
              ALL,
              {
                shipTo: [],
                shipFrom: [],
                productionRun: ['name'],
              },
            ],
            filters: [
              {
                field: 'shipTo',
                operator: QueryFilterOperators['='],
                value: settings.facility,
              },
              {
                field: 'approvedAt',
                operator: QueryFilterOperators.isNotEmpty,
                value: NullOperatorValues.isNotEmpty,
              },
              {
                field: 'ingestedAt',
                operator: QueryFilterOperators.isEmpty,
                value: NullOperatorValues.isEmpty,
              },
            ],
          }
        : skipToken
    );

    const {
      data: shipmentLinesToReceiveResponse,
      isSuccess: isGetShipmentLinesToReceiveSuccess,
      isFetching: isFetchingShipmentLinesToReceive,
      isError: isFetchingShipmentLinesToReceiveError,
      refetch: refetchShipmentLinesToReceive,
    } = useServerSideQuery<ShipmentLineSchema, ShipmentLineToReceive>(
      useGetShipmentLinesQuery,
      {
        pageSize: DISABLE_PAGE_LIMIT,
        schema: ['id', 'shipment'],
        filters: [
          {
            field: 'shipment.shipTo',
            operator: QueryFilterOperators['='],
            value: settings.facility,
          },
          {
            field: 'shipment.approvedAt',
            operator: QueryFilterOperators.isNotEmpty,
            value: NullOperatorValues.isNotEmpty,
          },
          {
            field: 'shipment.ingestedAt',
            operator: QueryFilterOperators.isEmpty,
            value: NullOperatorValues.isEmpty,
          },
        ],
      }
    );

    const shipmentsToReceive = useMemo(() => {
      const shipments = shipmentsToReceiveResponse?.data || [];
      const shipmentLines = shipmentLinesToReceiveResponse?.data || [];

      return shipments.map((shipment) => {
        const matchingLines = shipmentLines.filter(
          (line) => line.shipment === shipment.id
        );
        return {
          ...shipment,
          lines: matchingLines,
          shipmentType: getShipmentType(shipment),
        };
      });
    }, [
      shipmentsToReceiveResponse?.data,
      shipmentLinesToReceiveResponse?.data,
    ]);

    const isFetchingShipmentsToReceiveCombined =
      isFetchingShipmentsToReceive || isFetchingShipmentLinesToReceive;
    const isGetShipmentsToReceiveSuccessCombined =
      isGetShipmentsToReceiveSuccess && isGetShipmentLinesToReceiveSuccess;
    const isFetchingShipmentsToReceiveErrorCombined =
      isFetchingShipmentsToReceiveError ||
      isFetchingShipmentLinesToReceiveError;

    const handleRefetchShipmentsToReceive = () => {
      refetchShipmentsToReceive();
      refetchShipmentLinesToReceive();
    };

    return {
      shipmentsToReceive,
      isGetShipmentsToReceiveSuccess: isGetShipmentsToReceiveSuccessCombined,
      isFetchingShipmentsToReceive: isFetchingShipmentsToReceiveCombined,
      isFetchingShipmentsToReceiveError:
        isFetchingShipmentsToReceiveErrorCombined,
      refetchShipmentsToReceive: handleRefetchShipmentsToReceive,
    };
  };
